'use client';

import { useClientTranslation } from '@lib/i18n/client';
import {
  calculateCurrentPhaseFromDates,
  getCurrentStageByTrip,
  getPhaseDefinition,
  isValidPhase,
  phaseDefinitions,
} from '@lib/trip';
import { CopyBookingButton } from '@modules/phaser/components/copy-booking-button';
import { Alert } from '@tuic/web-compositions/core/Alert';
import { Fieldset } from '@tuic/web-compositions/core/Fieldset';
import { InputField } from '@tuic/web-compositions/core/InputField';
import { Form } from '@tuic/web-compositions/shared/Form';
import { SelectField } from '@tuic/web-compositions/shared/SelectField';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  getPhaserAttributesFromUrl,
  phaserAttributesSet,
  removePhaseAttributesFromUrl,
  setPhaserAttributesToUrl,
} from '../utils';

import type { PhaserAttributes, PhaserClientData } from '../types';
import type { Phase } from '@lib/trip';
import type {
  ChangeEventHandler,
  Dispatch,
  FC,
  MouseEventHandler,
  SetStateAction,
} from 'react';
import type React from 'react';

type Props = {
  data?: PhaserClientData;
};

type FormData = {
  phase?: number;
  trip?: string;
  stage?: number;
  datetime?: string;
  vip?: number;
  tariff?: string;
};

export const JourneyPreview: FC<Props> = ({ data }) => {
  const { t } = useClientTranslation();
  const [formData, setFormData] = useState<FormData>({});

  const formDisabled = typeof data === 'undefined';

  const isPhasing = useMemo(() => {
    if (typeof window === 'undefined') {
      return false;
    }

    const attrs = getPhaserAttributesFromUrl(window.location.href);

    return phaserAttributesSet(attrs);
  }, []);

  const vipOptions = useMemo(() => {
    return [
      { value: 0, children: 'Kein VIP-Status' },
      { value: 1, children: 'VIP 1' },
      { value: 2, children: 'VIP 2' },
    ];
  }, []);

  const tariffOptions = useMemo(() => {
    return [
      { value: 'PUR', children: 'PUR' },
      { value: 'PLUS', children: 'PLUS' },
      { value: 'PRO', children: 'PRO' },
    ];
  }, []);

  const dateTimeValue = useMemo(() => {
    if (typeof formData.datetime === 'undefined') {
      return undefined;
    }

    return DateTime.fromISO(formData.datetime).toFormat(
      // input[type="datetime-local"] compatible format
      "yyyy-MM-dd'T'HH:mm:ss",
    );
  }, [formData.datetime]);

  const handlePhaseChange = useCallback(
    createFieldChangeHandler('phase', data, setFormData),
    [data, setFormData],
  );

  const handleTripChange = useCallback(
    createFieldChangeHandler('trip', data, setFormData),
    [data, setFormData],
  );

  const handleStageChange = useCallback(
    createFieldChangeHandler('stage', data, setFormData),
    [data, setFormData],
  );

  const handleDateTimeChange = useCallback(
    createFieldChangeHandler('datetime', data, setFormData),
    [data, setFormData],
  );

  const handleVipChange = useCallback(
    createFieldChangeHandler('vip', data, setFormData),
    [data, setFormData],
  );

  const handleTariffChange = useCallback(
    createFieldChangeHandler('tariff', data, setFormData),
    [data, setFormData],
  );

  const handleReset = useCallback(createSubmitHandler(false, data, formData), [
    formData,
    data,
  ]);

  const handleSubmit = useCallback(createSubmitHandler(true, data, formData), [
    formData,
    data,
  ]);

  useEffect(() => {
    if (typeof data === 'undefined') {
      return;
    }

    const urlAttributes = getPhaserAttributesFromUrl(window.location.href);

    setFormData({
      datetime: urlAttributes.datetime ?? data.currentDateTime,
      vip: urlAttributes.vip ?? data.currentVip,
      tariff: urlAttributes.tariff ?? data.currentTariff,
    });
  }, [data]);

  // Calculated information
  const nowDate = DateTime.fromISO(
    formData.datetime || data?.currentDateTime || DateTime.now().toISO(),
  );
  const currentTrip =
    data?.trips.find(trip => {
      const start = DateTime.fromISO(
        trip.startDateTime || DateTime.now().toISO(),
      );
      const end = DateTime.fromISO(trip.endDateTime || DateTime.now().toISO());
      return nowDate >= start && nowDate <= end;
    }) || data?.trips?.[0];
  const currentPhase = currentTrip
    ? calculateCurrentPhaseFromDates({
        from: DateTime.fromISO(
          currentTrip.startDateTime || DateTime.now().toISO(),
        ),
        to: DateTime.fromISO(currentTrip.endDateTime || DateTime.now().toISO()),
        now: nowDate,
        checkedIn: 'auto',
      })
    : undefined;
  const currentStage = currentTrip
    ? getCurrentStageByTrip(currentTrip, nowDate)
    : undefined;

  const calculatedInformation: Record<string, React.ReactNode> = {
    [t('Phase')]: (
      <span
        className="underline decoration-primary-ocean-120 decoration-dotted"
        title={Object.values(phaseDefinitions)
          .map(p => p.name)
          .join('\n')}
      >
        {(currentPhase !== undefined &&
          getPhaseDefinition(currentPhase)?.name) ||
          'N/A'}
      </span>
    ),
    [t('Abschnitt')]: currentStage?.name || 'N/A',
    [t('Trip')]: currentTrip?.name || 'N/A',
  };

  return (
    <div className="space-y-8">
      {isPhasing && (
        <Alert>
          {t(
            'Das Reise-Vorschau-Werkzeug ist aktiv. Sie sehen gerade eine Vorschau für einen anderen Zustand dieser Reise.',
          )}
        </Alert>
      )}

      <Form
        data-track-form-enabled={false}
        header={{
          headline: t('Reise-Vorschau'),
          subline: (
            <div className="space-y-4">
              <div>
                {t(
                  'Wählen Sie aus den verschiedenen Phasen, Trips oder Abschnitten um eine Vorschau für einen anderen Zustand dieser Reise zu sehen. Klicken Sie auf "Zurücksetzen" um den Ursprungszustand wiederherzustellen.',
                )}
              </div>
              <div className="flex-none">
                <CopyBookingButton data={data} />
              </div>
              {currentTrip && (
                <div className="flex col sm:flex-row gap-1">
                  <span className="font-semibold">{t('Reisezeitraum')}:</span>
                  <span>
                    {new Date(currentTrip.startDateTime!).toLocaleString()} -{' '}
                    {new Date(currentTrip.endDateTime!).toLocaleString()}
                  </span>
                </div>
              )}
            </div>
          ),
        }}
        cancelButton={{
          children: t('Zurücksetzen'),
          onClick: handleReset,
        }}
        submitButton={{
          children: t('Anwenden'),
          onClick: handleSubmit,
        }}
      >
        <Fieldset>
          <div className="space-y-4 md:space-y-0 md:grid grid-cols-2 gap-x-6">
            <InputField
              type="datetime-local"
              label={t('Aktuelle Zeit')}
              note={t('Welche Zeit soll als aktuelle Zeit verwendet werden?')}
              disabled={formDisabled}
              onChange={handleDateTimeChange}
              value={dateTimeValue}
            />
            <div>
              <p className="font-semibold">{t('Berechnete Informationen')}:</p>
              {Object.entries(calculatedInformation).map(([name, val]) => (
                <p key={name}>
                  <strong className="font-semibold">{name}:</strong> {val}
                </p>
              ))}
            </div>
          </div>

          <div className="space-y-4 md:space-y-0 md:grid grid-cols-2 gap-x-6">
            <SelectField
              label={t('VIP-Status')}
              note={t('Mit welchem VIP-Status möchten Sie die Reise sehen?')}
              options={vipOptions}
              disabled={formDisabled}
              value={formData.vip ?? 0}
              onChange={handleVipChange}
            />
            <SelectField
              label={t('Tarif')}
              note={t('Mit welchem Tarif möchten Sie die Reise sehen?')}
              options={tariffOptions}
              disabled={formDisabled}
              value={formData.tariff}
              onChange={handleTariffChange}
            />
          </div>
        </Fieldset>
      </Form>
    </div>
  );
};

const renderSelectedPhaseNotice = (
  phases: PhaserClientData['phases'] | undefined,
  selected: string | Phase | undefined,
) => {
  if (!isValidPhase(selected) || typeof phases === 'undefined') {
    return undefined;
  }

  const phaseDefinition = phases[selected];

  return (
    <p>
      <strong className="font-semibold">{phaseDefinition.name}:</strong>{' '}
      {phaseDefinition.description}
    </p>
  );
};

const createFieldChangeHandler = (
  fieldName: keyof FormData,
  data: PhaserClientData | undefined,
  setFormData: Dispatch<SetStateAction<FormData>>,
): ChangeEventHandler<HTMLSelectElement | HTMLInputElement> => {
  return ev => {
    const value = ev.target.value;

    if (fieldName === 'vip') {
      setFormData(prev => ({
        ...prev,
        [fieldName]: parseInt(value, 10),
      }));

      return;
    }

    if (fieldName === 'datetime') {
      setFormData(prev => ({
        ...prev,
        datetime: new Date(value).toISOString(),
      }));
    }

    if (fieldName !== 'trip') {
      setFormData(prev => ({
        ...prev,
        [fieldName]: value,
      }));

      return;
    }

    setFormData(prev => ({
      ...prev,
      [fieldName]: value,
      stage: 0,
    }));
  };
};

const createSubmitHandler = (
  apply: boolean,
  data: PhaserClientData | undefined,
  formData: FormData,
): MouseEventHandler => {
  return ev => {
    ev.preventDefault();

    if (!apply) {
      window.location.href = removePhaseAttributesFromUrl(
        window.location.href,
      ).toString();
      return;
    }

    const attrs: PhaserAttributes = {
      datetime:
        formData.datetime !== data?.currentDateTime
          ? formData.datetime
          : undefined,
      vip: formData.vip !== data?.currentVip ? formData.vip : undefined,
      tariff:
        formData.tariff !== data?.currentTariff ? formData.tariff : undefined,
    };

    const url = setPhaserAttributesToUrl(window.location.href, attrs);

    window.location.href = url.toString();
  };
};
